/* =========================================================================
   ValueStack — the itemized "everything you get" list that sits beside the
   founding-seat price card on the landing and subscriptions pages.

   🚩 SHELVED, NOT DELETED — SHOW_STACK_PRICES hides the Hormozi value stack
   (the $ amount on every line plus the "Total value $4,188" row) without
   removing it. The price is justified by the college / hire-an-engineer
   comparison in the .lp7-anchor section instead, so the self-assigned dollar
   values aren't needed. The deliverables still show — buyers need to know
   what $497 buys, it's only the pricing tally that's gone.

   Flip SHOW_STACK_PRICES to `true` to bring the value stack back exactly as it
   was, on every page below at once. Nothing else has to be restored: the i18n
   keys (lp6.stackVal1-3 and 6-8, lp6.stackTotalValue) and the CSS (-stack-val,
   -stack-total, -price-strike) are all still in place. The price cards read
   this same flag to swap their $4,188 strikethrough back in.

   The pages use different class prefixes for identical markup — lp7
   (LandingPage7), lp9 (LandingPage9) and lp6 (LandingPage6, Subscriptions3) —
   hence the `prefix` prop.

   `reveal` opts the card into the scroll reveal (data-reveal, animated by
   hooks/useReveal in the page). It is off by default so a page that doesn't
   run the hook, or animates its own way, renders exactly as before.
   ========================================================================= */

import React from "react";

// reactstrap components
import { Col } from "reactstrap";

// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

import { useTranslation } from "react-i18next";

export const SHOW_STACK_PRICES = false;

// Each line's name/value live in common.json (lp6.stack*).
//
// 6, 7 and 8 (First Paying Clients Kit / "Build Apps for Local Businesses"
// playbook / 20 App-Idea Library) are off the list. Their i18n keys stay put,
// like the shelved prices above, so a line comes back by putting its number
// back here.
//
// 4, 5, 9 and 10 (live group mentorship, lifetime community, billing
// templates, 1-on-1 welcome session) were removed, keys and all, because none
// of them is delivered. Do not bring them back without the real thing.
//
// If SHOW_STACK_PRICES is ever flipped on, lp6.stackTotalValue must be
// recomputed from the lines that are left.
const STACK = [1, 2, 3];

const ValueStack = ({ prefix = "lp7", reveal = false }) => {
  const { t } = useTranslation();

  return (
    <Col lg="7">
      <div
        className={`${prefix}-stack`}
        data-reveal={reveal ? "" : undefined}
      >
        {STACK.map((n) => (
          <div className={`${prefix}-stack-row`} key={n}>
            <span className={`${prefix}-stack-check`}>
              <FontAwesomeIcon icon={faCheck} />
            </span>
            <span className={`${prefix}-stack-name`}>
              {/* A page can override a single line by defining
                  <prefix>.stackItem<n>; anything it doesn't define falls
                  through to the shared lp6 copy. LandingPage9 uses this to
                  say "English or Spanish" on its EN page only. */}
              {t([`${prefix}.stackItem${n}`, `lp6.stackItem${n}`])}
            </span>
            {SHOW_STACK_PRICES && (
              <span className={`${prefix}-stack-val`}>
                {t(`lp6.stackVal${n}`)}
              </span>
            )}
          </div>
        ))}

        {SHOW_STACK_PRICES && (
          <div className={`${prefix}-stack-total`}>
            <span className={`${prefix}-stack-name`}>
              {t("lp6.stackTotalLabel")}
            </span>
            <span className={`${prefix}-stack-val total`}>
              {t("lp6.stackTotalValue")}
            </span>
          </div>
        )}
      </div>
    </Col>
  );
};

export default ValueStack;
